import { FC, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';

import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';
import OperationMenuDots from 'modules/_shared/components/MenuDots/OperationMenuDots';
import { EquityMarketStatus } from '../types/EquityMarketAdminPanelContextType';
import EquityMarketStatusBadge, {
  equityMarketStatusConfig,
} from './EquityMarketStatusBadge';
import EquityMarketAdministratorBadge from './EquityMarketAdministratorBadge';

type EquityMarketAdminPanelHeaderProps = {
  societyId: string;
  societyName: string;
  equityMarketStatus?: EquityMarketStatus;
  onEquityMarketStatusChange?: (status: EquityMarketStatus) => void; // eslint-disable-line no-unused-vars
};

const EquityMarketAdminPanelHeader: FC<EquityMarketAdminPanelHeaderProps> = ({
  societyId,
  societyName,
  equityMarketStatus = 'OPEN',
  onEquityMarketStatusChange,
}) => {
  const { t } = useTranslate();
  const location = useLocation();
  const partnerQuery = new URLSearchParams(location.search).get('partnerId');
  const pq = partnerQuery ? `?partnerId=${partnerQuery}` : '';

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: societyName, to: `/sociedad/${societyId}` },
      {
        label: t('EquityMarket'),
        to: `/mercado-de-acciones/${societyId}${pq}`,
      },
      { label: t('AdminPanel'), isActive: true },
    ],
    [t, societyName, societyId, pq]
  );

  return (
    <>
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="nk-block-head page-header-subtitle">
        <div className="nk-block-between">
          <div className="nk-block-head-content">
            <div className="d-flex align-items-center">
              <h3 className="nk-block-title page-title">{t('AdminPanel')}</h3>

              <EquityMarketAdministratorBadge />
              <EquityMarketStatusBadge status={equityMarketStatus} />
            </div>

            <div className="nk-block-des text-soft">
              <p>{t('EquityAdminPanelDescription')}</p>
            </div>
          </div>

          {onEquityMarketStatusChange && (
            <div className="nk-block-head-content d-flex align-items-center gap-2">
              {/* @ts-ignore */}
              <OperationMenuDots
                title={t('MarketStatus')}
                variant="primary"
                menuOptions={Object.entries(equityMarketStatusConfig).map(
                  ([status, config]) => ({
                    icon: config.icon,
                    text: t(config.labelKey),
                    action: () =>
                      onEquityMarketStatusChange(status as EquityMarketStatus),
                    dropdown: true,
                    disabled:
                      equityMarketStatus === (status as EquityMarketStatus),
                  })
                )}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EquityMarketAdminPanelHeader;
